/**
 * Build a single PATCH for "Accept All AI Suggestions" on a staged product detail panel.
 * Promotes description/features drafts and applies concrete verification fixes + tag merges.
 */

import { buildTagsFromAiSuggestedFix } from './aiTagSuggestedFix';
import { snapshotScrapedFieldsIfNeeded } from './scrapedContentBackup';
import { clampShortDescription, isShortDescriptionIssueField } from './shortDescription';
import { resolveCategorySuggestedFix } from './categorySuggestedFix';

const TAG_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export interface AcceptAllAiIssue {
  field: string;
  severity?: string;
  message?: string;
  autoFixable?: boolean;
  suggestedFix?: unknown;
}

export interface AcceptAllAiProduct {
  description?: string | null;
  shortDescription?: string | null;
  scrapedDescription?: string | null;
  scrapedShortDescription?: string | null;
  scrapedFeatures?: unknown;
  aiDescription?: string | null;
  aiShortDescription?: string | null;
  features?: unknown;
  aiFeatures?: unknown;
  inTheBox?: unknown;
  aiInTheBox?: unknown;
  tags?: unknown;
  category?: string | null;
  verification?: {
    issues?: AcceptAllAiIssue[];
    suggestions?: {
      suggestedCategory?: string;
      suggestedTags?: string[];
    };
  } | null;
}

export interface AcceptAllAiResult {
  patch: Record<string, unknown>;
  applied: string[];
  skipped: string[];
}

function asTrimmedText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  return '';
}

function asStringArray(v: unknown): string[] {
  if (v == null) return [];
  if (Array.isArray(v)) return v.map((x) => (x == null ? '' : String(x))).filter(Boolean);
  if (typeof v === 'string') return v.trim() ? [v.trim()] : [];
  return [];
}

function featuresKey(list: string[]): string {
  return list.map((s) => s.trim()).filter(Boolean).join('\n');
}

function asInTheBoxItems(
  v: unknown
): Array<{ name: string; quantity?: number; sku?: string }> {
  if (!Array.isArray(v)) return [];
  return v
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const r = row as { name?: unknown; quantity?: unknown; sku?: unknown };
      const name = String(r.name ?? '').trim();
      if (!name) return null;
      const item: { name: string; quantity?: number; sku?: string } = { name };
      if (typeof r.quantity === 'number' && r.quantity > 0) item.quantity = Math.round(r.quantity);
      if (typeof r.sku === 'string' && r.sku.trim()) item.sku = r.sku.trim();
      return item;
    })
    .filter(Boolean) as Array<{ name: string; quantity?: number; sku?: string }>;
}

function inTheBoxKey(items: Array<{ name: string; quantity?: number; sku?: string }>): string {
  return items
    .map((i) => `${i.name.trim().toLowerCase()}|${i.quantity ?? 1}`)
    .join('\n');
}

function tagsKey(list: string[]): string {
  return [...list].map((s) => s.toLowerCase()).sort().join('|');
}

function isPlausibleTagSlug(s: string): boolean {
  const t = s.trim().toLowerCase();
  if (!t || t.length > 48) return false;
  return TAG_SLUG_PATTERN.test(t);
}

/**
 * Pull tag slugs to strip from issue messages like:
 * - "remove 'other' from tags"
 * - "Tags 'instant', 'tripod', and 'drone' are irrelevant"
 * - "strip other"
 */
export function extractTagsToStripFromIssueMessage(message: string | undefined): string[] {
  if (!message) return [];
  const found = new Set<string>();

  const removeMatch =
    message.match(/remove\s+['"]?([\w-]+)['"]?\s+from\s+tags?/i) ||
    message.match(/remove\s+tag\s+['"]?([\w-]+)['"]?/i) ||
    message.match(/strip(?:ped)?\s+['"]?([\w-]+)['"]?/i);
  if (removeMatch && isPlausibleTagSlug(removeMatch[1])) {
    found.add(removeMatch[1].toLowerCase());
  }

  // Quoted slugs anywhere in the message (irrelevant / strip lists).
  // Use exec (not matchAll) so CRA's older TS target does not need downlevelIteration.
  const quotedSlug = /['"]([a-z0-9]+(?:-[a-z0-9]+)*)['"]/gi;
  let quotedMatch: RegExpExecArray | null;
  while ((quotedMatch = quotedSlug.exec(message)) !== null) {
    if (isPlausibleTagSlug(quotedMatch[1])) found.add(quotedMatch[1].toLowerCase());
  }

  // Bare "other" called out as a tag to remove.
  if (/\bother\b/i.test(message) && /tag/i.test(message)) {
    found.add('other');
  }

  return Array.from(found).filter((slug) => slug !== 'pro-only');
}

/**
 * Whether the product has any pending AI content / fixable suggestion worth accepting.
 */
export function hasPendingAiSuggestions(product: AcceptAllAiProduct): boolean {
  const result = buildAcceptAllAiPatch(product);
  return Object.keys(result.patch).length > 0;
}

export function buildAcceptAllAiPatch(product: AcceptAllAiProduct): AcceptAllAiResult {
  const patch: Record<string, unknown> = {};
  const applied: string[] = [];
  const skipped: string[] = [];

  const aiDescription = asTrimmedText(product.aiDescription);
  const description = asTrimmedText(product.description);
  const aiShort = asTrimmedText(product.aiShortDescription);
  const shortDescription = asTrimmedText(product.shortDescription);
  const clampedAiShort = aiShort ? clampShortDescription(aiShort) : '';

  if (aiDescription && aiDescription !== description) {
    patch.description = aiDescription;
    patch.descriptionSource = 'ai_generated';
    applied.push('description');
    if (clampedAiShort) {
      patch.shortDescription = clampedAiShort;
    }
  } else if (clampedAiShort && clampedAiShort !== shortDescription) {
    patch.shortDescription = clampedAiShort;
    applied.push('shortDescription');
  }

  const catalogFeatures = asStringArray(product.features);
  const aiFeatures = asStringArray(product.aiFeatures);
  if (aiFeatures.length > 0 && featuresKey(aiFeatures) !== featuresKey(catalogFeatures)) {
    patch.features = [...aiFeatures];
    applied.push('features');
  }

  const catalogBox = asInTheBoxItems(product.inTheBox);
  const aiBox = asInTheBoxItems(product.aiInTheBox);
  if (aiBox.length > 0 && inTheBoxKey(aiBox) !== inTheBoxKey(catalogBox)) {
    patch.inTheBox = aiBox.map((i) => ({ ...i }));
    patch.aiInTheBox = null;
    applied.push('inTheBox');
  }

  let tagsAccum = asStringArray(product.tags);
  const originalTagsKey = tagsKey(tagsAccum);
  const issues = product.verification?.issues || [];

  for (const issue of issues) {
    if (!issue.autoFixable || issue.suggestedFix == null) {
      // Tag issues with null suggestedFix may still be handled by message-based strip below.
      continue;
    }

    if (issue.field === 'tags') {
      const next = buildTagsFromAiSuggestedFix(tagsAccum, issue.suggestedFix);
      if (next) {
        tagsAccum = next;
        if (!applied.includes('tags')) applied.push('tags');
      } else {
        skipped.push('tags (unparseable suggestedFix)');
      }
      continue;
    }

    if (isShortDescriptionIssueField(issue.field)) {
      const raw =
        typeof issue.suggestedFix === 'string' && issue.suggestedFix.trim()
          ? issue.suggestedFix.trim()
          : aiShort;
      const fix = raw ? clampShortDescription(raw) : '';
      if (fix && fix !== ((patch.shortDescription as string | undefined) || shortDescription)) {
        patch.shortDescription = fix;
        if (!applied.includes('description') && !applied.includes('shortDescription')) {
          applied.push('shortDescription');
        }
      }
      continue;
    }

    if (issue.field === 'category') {
      const cat = resolveCategorySuggestedFix({
        suggestedFix: issue.suggestedFix,
        suggestedCategory: product.verification?.suggestions?.suggestedCategory,
        message: issue.message,
        currentCategory: product.category,
      });
      if (cat) {
        patch.category = cat;
        if (!applied.includes('category')) applied.push('category');
      }
      continue;
    }

    if (issue.field.includes('.')) {
      const parts = issue.field.split('.');
      const root = parts[0];
      const productRecord = product as Record<string, unknown>;
      const current =
        (patch[root] as Record<string, unknown> | undefined) ||
        ((productRecord[root] as Record<string, unknown> | undefined) ?? {});
      patch[root] = { ...current, [parts[1]]: issue.suggestedFix };
      if (!applied.includes(root)) applied.push(root);
      continue;
    }

    patch[issue.field] = issue.suggestedFix;
    if (!applied.includes(issue.field)) applied.push(issue.field);
  }

  // Strip noise tags called out in tag issue messages (even when suggestedFix is prose).
  const toStrip = new Set<string>();
  for (const issue of issues) {
    if (issue.field !== 'tags') continue;
    for (const slug of extractTagsToStripFromIssueMessage(issue.message)) {
      toStrip.add(slug);
    }
  }
  if (toStrip.size > 0) {
    const before = tagsKey(tagsAccum);
    tagsAccum = tagsAccum.filter((t) => !toStrip.has(String(t).toLowerCase()));
    if (tagsKey(tagsAccum) !== before && !applied.includes('tags')) {
      applied.push('tags');
    }
  }

  // Merge suggested tags (same as Suggestions → Merge All).
  const suggestedTags = asStringArray(product.verification?.suggestions?.suggestedTags)
    .filter((tag) => tag.trim().toLowerCase() !== 'pro-only');
  if (suggestedTags.length > 0) {
    const merged = Array.from(new Set([...tagsAccum, ...suggestedTags.map((t) => t.trim()).filter(Boolean)]));
    const newOnes = suggestedTags.filter(
      (t) => !tagsAccum.some((c) => c.toLowerCase() === t.toLowerCase())
    );
    if (newOnes.length > 0) {
      tagsAccum = merged;
      if (!applied.includes('tags')) applied.push('tags');
    }
  }

  // Suggested category when not already applied via an issue.
  const suggestedCategory = resolveCategorySuggestedFix({
    suggestedCategory: product.verification?.suggestions?.suggestedCategory,
    currentCategory: product.category,
  });
  if (suggestedCategory && patch.category == null) {
    patch.category = suggestedCategory;
    if (!applied.includes('category')) applied.push('category');
  }

  if (patch.category == null) {
    for (const issue of issues) {
      if (issue.field !== 'category') continue;
      const cat = resolveCategorySuggestedFix({
        suggestedFix: issue.suggestedFix,
        suggestedCategory: product.verification?.suggestions?.suggestedCategory,
        message: issue.message,
        currentCategory: product.category,
      });
      if (cat) {
        patch.category = cat;
        if (!applied.includes('category')) applied.push('category');
        break;
      }
    }
  }

  if (tagsKey(tagsAccum) !== originalTagsKey) {
    patch.tags = tagsAccum;
  }

  // Tag issues with no concrete fix and no strippable slugs in the message.
  for (const issue of issues) {
    if (issue.field !== 'tags') continue;
    if (issue.autoFixable && issue.suggestedFix != null) continue;
    const strips = extractTagsToStripFromIssueMessage(issue.message);
    if (strips.length > 0) continue;
    const label = (issue.message || 'tag warning').slice(0, 80);
    if (!skipped.some((s) => s.startsWith('tags ('))) {
      skipped.push(`tags (${label})`);
    }
  }

  return { patch: snapshotScrapedFieldsIfNeeded(product, patch), applied, skipped };
}
